import type { ReactElement } from 'react';
import type {
  CabinIssueSeverity,
  CabinValidationIssue,
} from '../../../domain/models';
import { SEVERITY_LABELS } from './cabinFormat';

export interface ValidationAlertsProps {
  readonly issues: readonly CabinValidationIssue[];
  readonly isValid: boolean;
}

const SEVERITY_GLYPH: Readonly<Record<CabinIssueSeverity, string>> = {
  ERROR: '✕',
  WARNING: '!',
  INFO: 'i',
};

function IssueList({
  severity,
  issues,
}: {
  readonly severity: CabinIssueSeverity;
  readonly issues: readonly CabinValidationIssue[];
}): ReactElement | null {
  if (issues.length === 0) {
    return null;
  }
  return (
    <ul className="ce-issues" data-severity={severity}>
      {issues.map((issue, index) => (
        <li
          key={`${issue.code}-${issue.field ?? 'cabin'}-${index}`}
          className="ce-issue"
          data-severity={severity}
        >
          <span className="ce-issue__glyph" aria-hidden="true">
            {SEVERITY_GLYPH[severity]}
          </span>
          <span className="ce-issue__body">
            <span className="ce-visually-hidden">{SEVERITY_LABELS[severity]}: </span>
            {issue.message}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Live validation output: errors and warnings first, informational notes collapsed. */
export function ValidationAlerts({ issues, isValid }: ValidationAlertsProps): ReactElement {
  const errors = issues.filter((issue) => issue.severity === 'ERROR');
  const warnings = issues.filter((issue) => issue.severity === 'WARNING');
  const notes = issues.filter((issue) => issue.severity === 'INFO');

  return (
    <section className="ce-card" aria-labelledby="ce-validation-title">
      <div className="ce-validation__header">
        <h3 id="ce-validation-title" className="ce-card__title">
          Validation
        </h3>
        <p className="ce-validation__counts" aria-live="polite">
          <span className="ce-count" data-severity="ERROR">
            {errors.length} {errors.length === 1 ? 'error' : 'errors'}
          </span>
          <span className="ce-count" data-severity="WARNING">
            {warnings.length} {warnings.length === 1 ? 'warning' : 'warnings'}
          </span>
        </p>
      </div>

      {isValid ? (
        <p className="ce-validation__ok" role="status">
          ✓ Layout is valid and can be applied.
        </p>
      ) : (
        <p className="ce-validation__blocked" role="status">
          Fix the errors below before applying this layout.
        </p>
      )}

      <div role={errors.length > 0 ? 'alert' : undefined}>
        <IssueList severity="ERROR" issues={errors} />
      </div>
      <IssueList severity="WARNING" issues={warnings} />

      {notes.length > 0 ? (
        <details className="ce-notes">
          <summary>
            {notes.length} {notes.length === 1 ? 'note' : 'notes'}
          </summary>
          <IssueList severity="INFO" issues={notes} />
        </details>
      ) : null}
    </section>
  );
}
